/**
 * Пошаговый мастер анкеты (§16.5, §16.6).
 *
 * Одна группа вопросов на экран, автосохранение после каждого шага,
 * ответ «не знаю» там, где он разрешён конфигом.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, deviceLabel } from '../api/client'
import type { AnswerValue, NextQuestion, Questionnaire } from '../api/types'
import { QuestionField } from './QuestionField'

interface Props {
  draftId: string
  questionnaire: Questionnaire
}

export function IntakeWizard({ draftId, questionnaire }: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Ссылка «Изменить анкету» с результата (§16.5) ведёт сюда с ?edit=1:
  // анкета уже полностью отвечена, next_question ничего не вернёт и без
  // этого режима мастер тут же отправит обратно на результат, ничего не
  // дав отредактировать. В режиме правки стартуем с последнего отвеченного
  // вопроса, а «Назад» даёт пройти к любому более раннему ответу.
  const editMode = searchParams.get('edit') != null
  const [step, setStep] = useState<NextQuestion | null>(null)
  const [value, setValue] = useState<AnswerValue>(null)
  const [revision, setRevision] = useState(0)
  const [answered, setAnswered] = useState(0)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Стек пройденных экранов — для кнопки «Назад» (§16.6). Ветвление
  // анкеты зависит от ответов, поэтому «назад» не запрашивает вопрос у
  // сервера заново: он просто показывает уже пройденный экран с ранее
  // сохранённым ответом, а «Дальше» пересчитает путь как обычно.
  const historyRef = useRef<NextQuestion[]>([])
  const answersRef = useRef<Record<string, AnswerValue>>({})
  const [canGoBack, setCanGoBack] = useState(false)

  const loadStep = useCallback(async () => {
    setError(null)
    const [next, draft] = await Promise.all([
      api.nextQuestion(draftId),
      api.getDraft(draftId),
    ])
    setRevision(draft.revision)
    setAnswered(Object.keys(draft.ответы).length)
    answersRef.current = draft.ответы
    setValue(null)
    setStep(next)
    if (next.вопрос != null) {
      const top = historyRef.current[historyRef.current.length - 1]
      if (top?.вопрос?.id !== next.вопрос.id) {
        historyRef.current = [...historyRef.current, next]
        setCanGoBack(historyRef.current.length > 1)
      }
    }
    // Анкета заполнена — сразу к результату подбора (§10, §16.5).
    if (next['анкета заполнена']) navigate(`/result/${draftId}`)
  }, [draftId, navigate])

  const loadEditStep = useCallback(async () => {
    setError(null)
    const draft = await api.getDraft(draftId)
    setRevision(draft.revision)
    setAnswered(Object.keys(draft.ответы).length)
    answersRef.current = draft.ответы

    // Только что заведённая анкета правкой быть не может — обычный старт.
    const allQuestions = questionnaire.blocks.flatMap((block) => block.questions)
    const answeredQuestions = allQuestions.filter((q) => q.id in draft.ответы)
    if (answeredQuestions.length === 0) {
      await loadStep()
      return
    }

    historyRef.current = answeredQuestions.map((q) => {
      const block = questionnaire.blocks.find((b) => b.id === q.block)
      return {
        вопрос: q,
        блок: block != null ? { id: block.id, title: block.title } : null,
        'анкета заполнена': false,
      }
    })
    const last = historyRef.current[historyRef.current.length - 1]
    setCanGoBack(historyRef.current.length > 1)
    setStep(last)
    setValue(last.вопрос != null ? (answersRef.current[last.вопрос.id] ?? null) : null)
  }, [draftId, questionnaire, loadStep])

  useEffect(() => {
    const init = editMode ? loadEditStep : loadStep
    init().catch((err) => setError(String(err.message ?? err)))
    // Только при первом открытии экрана — дальше и «Дальше», и «Назад»
    // ведут себя как обычно вне зависимости от того, как сюда попали.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId])

  function goBack() {
    if (historyRef.current.length <= 1) return
    historyRef.current = historyRef.current.slice(0, -1)
    const previous = historyRef.current[historyRef.current.length - 1]
    setCanGoBack(historyRef.current.length > 1)
    setError(null)
    setStep(previous)
    setValue(previous.вопрос != null ? (answersRef.current[previous.вопрос.id] ?? null) : null)
  }

  const question = step?.вопрос ?? null

  async function save(answer: AnswerValue) {
    if (question == null) return
    setSaving(true)
    setError(null)
    try {
      // Автосохранение после каждого шага: анкета живёт на сервере,
      // и её можно продолжить с другого устройства (§16.6).
      const result = await api.saveAnswers(
        draftId,
        { [question.id]: answer },
        revision,
        deviceLabel(),
      )
      setNotice(result.конфликт ?? null)
      await loadStep()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  if (error != null && step == null) return <p className="error">{error}</p>
  if (question == null) {
    return <p className="muted">{editMode ? 'Загружаю…' : 'Анкета заполнена, перехожу к результату…'}</p>
  }

  const total = questionnaire.blocks.reduce((sum, block) => sum + block.questions.length, 0)
  const progress = Math.min(100, Math.round((answered / Math.max(total, 1)) * 100))
  const canSubmit = value !== null || question.type === 'bool'

  return (
    <div>
      <div className="progress" role="progressbar" aria-valuenow={progress}>
        <div className="progress-value" style={{ width: `${progress}%` }} />
      </div>

      <p className="muted">
        {step?.блок?.title} · ответов: {answered}
      </p>

      {notice != null && <div className="notice">{notice}</div>}

      <h2 className="question-text">{question.text}</h2>
      {question.effect != null && <p className="question-effect">{question.effect}</p>}

      {question.stop_condition === true && (
        <div className="notice">
          Ответ «да» не остановит подбор, но проблему нужно закрыть до подачи заявки в банк.
        </div>
      )}

      <QuestionField
        question={question}
        value={value}
        unknownMarker={questionnaire.unknown_answer_marker}
        onChange={setValue}
      />

      {error != null && <p className="error">{error}</p>}

      <div className="actions">
        {canGoBack && (
          <button disabled={saving} onClick={goBack}>
            Назад
          </button>
        )}

        <button
          className="primary"
          disabled={saving || !canSubmit}
          onClick={() => save(value)}
        >
          {saving ? 'Сохраняю…' : 'Дальше'}
        </button>

        {/* Ответ «не знаю» допускается только там, где это разрешено конфигом (§7). */}
        {question.allow_unknown && (
          <button
            disabled={saving}
            onClick={() => save(questionnaire.unknown_answer_marker)}
          >
            Не знаю
          </button>
        )}
      </div>

      {question.source != null && (
        <p className="disclaimer">Источник требования: {question.source}</p>
      )}
    </div>
  )
}
